import { AddArticleService } from '@configurator/project/add-article.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { CreateProjectService } from '@project/service/create-project.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { WizardModel } from '@configuration/wizard/models/wizard-model';
import { Injectable } from '@angular/core';
import { ConfigurationTemplate } from '../wizard-model.interface';
import { ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { RouteNavigationService } from '@services/route-navigation.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { lastValueFrom } from 'rxjs';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConfigurationCreationService } from '@configuration/wizard/service/configuration-creation.service';
import { Log } from '@shared/debug/log';
import { ProjectModel } from '@project/model/project.model';
import { OpenLabelEditorService } from '@project/components/label/label-editor/service/open-label-editor.service';

@Injectable()
export class SplicingConnector221Model extends WizardModel {

  public type = 'SPLICING_CONNECTOR_221';

  private _selectedArticle: ArticleWithTechnicalPropertiesDTO | undefined = undefined;

  constructor(
    private overlayService: OverlayService,
    private configurationCreationService: ConfigurationCreationService,
    private routeNavigationService: RouteNavigationService,
    private articleDataService: ArticleDataService,
    private projectModel: ProjectModel,
    private openLabelEditorService: OpenLabelEditorService,
    private readonly createProjectService: CreateProjectService,
    private readonly addArticleService: AddArticleService,
    private readonly selectionModelService: SelectionModelService,
    private readonly projectModelService: ProjectModelService,
  ) {
    super();
  }

  protected createConfig(): ConfigurationTemplate {
    return {
      id: 'create-project-splicing-connector-221',
      type: 'type',
      imageUrl: 'ng-assets/img/projecttypes/splicing-connector-221.webp',
      label: 'configurationCreation.type.splicing_connector_221',
      steps: [
        {
          type: 'meta',
          id: 'meta',
          label: 'configurationCreation.progress.configuration_data',
        },
        {
          type: 'selectFromAssistant',
          id: 'selectFromAssistant',
          label: 'configurationCreation.progress.select_article',
          assistant: 'SplicingConnector221Assistant',
          technicalRequirements: [],
        }
      ],
      neededPermission: '221',
    };
  }

  public async create(): Promise<void> {
    const article = this._selectedArticle;
    if (article === undefined) {
      return;
    }
    if (this.creationStarted) {
      return;
    }
    this.creationStarted = true;
    await this.routeNavigationService.toConfigurator();

    try {
      await this.overlayService.doWithOverlay(async () => {
        await this.createFreeMountingConfiguration();
        this.projectModel.currentProjectType = 'SPLICING_CONNECTOR_221';
        const articleUsageIds = await this.addArticleToConfiguration(article);
        this.selectionModelService.selectArticlesByUsageIds(articleUsageIds);
        await this.openLabelEditorService.openForCurrentSelection();
      }, OverlayBackground.color);
    } catch (error) {
      Log.error(error);
    } finally {
      this.creationStarted = false;
    }
  }

  private async createFreeMountingConfiguration(): Promise<void> {
    await this.configurationCreationService.prepareNewConfiguration(
      this.metaData,
    );
    await this.createProjectService.createFreeMounting();
  }

  private async addArticleToConfiguration(article: ArticleWithTechnicalPropertiesDTO): Promise<string[]> {
    const articlesToAdd: ArticleVO[] = await lastValueFrom(this.articleDataService.getLatestArticles([article.articleId]));
    ProjectUtils.setBaseArticleProperties(this.projectModel.currentProject, articlesToAdd[0]);
    await this.projectModelService.updateBaseArticle();

    const articleGuids = await this.addArticleService.addArticlesToMountingRail(articlesToAdd);
    return articleGuids;
  }

  public selectValue(_step: number, value: ArticleWithTechnicalPropertiesDTO | undefined): void {
    this._selectedArticle = value;
  }
}
